import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import {
  CHURCH_RECRUIT_CONFIG,
  INSTITUTE_RECRUIT_CONFIG,
  MINISTER_RECRUIT_CONFIG,
  OVERSEAS_RECRUIT_CONFIG,
} from '../../screens/recruit/common/recruitConfigs';
import type { RecruitBoardConfig } from '../../screens/recruit/common/RecruitTypes';
import { sortList } from '../../DefaultData';
import type { RecruitSavedListItem } from './recruitSavedAdminShared';

type RecruitBoardKey = 'minister' | 'overseas' | 'church' | 'institute';

type Props = {
  sourceBoard: RecruitBoardKey;
  item: RecruitSavedListItem | null;
  onClose: () => void;
  onMoved: () => void;
};

const BOARD_BY_KEY: Record<RecruitBoardKey, RecruitBoardConfig> = {
  minister: MINISTER_RECRUIT_CONFIG,
  overseas: OVERSEAS_RECRUIT_CONFIG,
  church: CHURCH_RECRUIT_CONFIG,
  institute: INSTITUTE_RECRUIT_CONFIG,
};

const BOARD_LABELS: Record<RecruitBoardKey, string> = {
  minister: '국내사역지',
  overseas: '해외사역지',
  church: '찬양대/방송/직원',
  institute: '학교/기관/단체',
};

const CHURCH_JOB_SORTS = ['찬양대', '방송', '직원'];

/** 사역 조건·급여 등 사역자 전용 항목이 있는 게시판 */
const isMinisterBoard = (key: RecruitBoardKey) => key === 'minister' || key === 'overseas';

const getAdminMovePostRoute = (apiBase: string) => `${apiBase}/recruitadminmovepost`;

const pickDefaultMinisterSort = (item: RecruitSavedListItem) => {
  if (item.sort && sortList.includes(item.sort)) return item.sort;
  return sortList[0] ?? '담임';
};

const pickDefaultChurchSort = (item: RecruitSavedListItem) => {
  const fromTitle = CHURCH_JOB_SORTS.find((job) => item.title?.includes(job));
  if (fromTitle) return fromTitle;
  if (item.sort && CHURCH_JOB_SORTS.includes(item.sort)) return item.sort;
  return CHURCH_JOB_SORTS[0] ?? '찬양대';
};

export default function RecruitSavedMoveModal({ sourceBoard, item, onClose, onMoved }: Props) {
  const sourceConfig = BOARD_BY_KEY[sourceBoard];

  const targetOptions = useMemo(() => {
    return (Object.keys(BOARD_BY_KEY) as RecruitBoardKey[]).filter((key) => key !== sourceBoard);
  }, [sourceBoard]);

  const [targetBoard, setTargetBoard] = useState<RecruitBoardKey>(targetOptions[0] ?? 'church');
  const [selectedSort, setSelectedSort] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const needsSort = isMinisterBoard(targetBoard) || targetBoard === 'church';
  const sortOptions = isMinisterBoard(targetBoard) ? sortList : CHURCH_JOB_SORTS;
  const sortLabel = '직무 (이동 후)';

  useEffect(() => {
    if (!item) return;
    setTargetBoard(targetOptions[0] ?? 'church');
  }, [item, targetOptions]);

  useEffect(() => {
    if (!item) return;
    if (isMinisterBoard(targetBoard)) {
      setSelectedSort(pickDefaultMinisterSort(item));
    } else if (targetBoard === 'church') {
      setSelectedSort(pickDefaultChurchSort(item));
    } else {
      setSelectedSort('');
    }
  }, [item, targetBoard]);

  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, submitting, onClose]);

  if (!item?.id) return null;

  const movePost = async () => {
    if (needsSort && !selectedSort) {
      alert('이동할 게시판의 직무를 선택해주세요.');
      return;
    }

    const sourceLabel = BOARD_LABELS[sourceBoard];
    const targetLabel = BOARD_LABELS[targetBoard];
    const sortNote = needsSort ? `\n직무: ${selectedSort}` : '';
    const fieldNote =
      isMinisterBoard(sourceBoard) && !isMinisterBoard(targetBoard)
        ? '\n\n사역 조건·급여 등 사역자 전용 항목은 이동되지 않습니다.'
        : !isMinisterBoard(sourceBoard) && isMinisterBoard(targetBoard)
          ? '\n\n사역 조건·급여 등 사역자 전용 항목은 비워진 상태로 이동됩니다.'
          : '';

    const confirmed = window.confirm(
      `「${item.title}」 글을\n${sourceLabel} → ${targetLabel}(으)로 옮기시겠습니까?${sortNote}${fieldNote}`,
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const route = getAdminMovePostRoute(sourceConfig.apiBase);
      const res = await axios.post(`${MainURL}/${route}`, {
        postId: item.id,
        sourceBoard,
        targetBoard,
        sort: needsSort ? selectedSort : undefined,
      });

      if (res.data?.ok) {
        alert(res.data.message || '게시글이 이동되었습니다.');
        onMoved();
        onClose();
      } else {
        alert(res.data?.message || '이동에 실패했습니다.');
      }
    } catch {
      alert('이동 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-board-manage__modal-backdrop" onClick={submitting ? undefined : onClose}>
      <div
        className="admin-board-manage__modal admin-board-manage__modal--move"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recruit-move-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-board-manage__modal-head">
          <h3 id="recruit-move-title">게시판 이동</h3>
          <button
            type="button"
            className="admin-board-manage__modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        <div className="admin-board-manage__modal-body">
          <div className="admin-notice-post__form admin-board-manage__move-form">
            <p className="admin-board-manage__modal-meta">
              현재: <strong>{BOARD_LABELS[sourceBoard]}</strong> · 글 번호 {item.id}
            </p>
            <p className="admin-board-manage__move-title" title={item.title}>
              {item.title}
            </p>

            <div className="admin-notice-post__field">
              <p className="admin-notice-post__label">이동할 게시판 *</p>
              <div className="admin-notice-post__chips">
                {targetOptions.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`admin-notice-post__chip${targetBoard === key ? ' admin-notice-post__chip--on' : ''}`}
                    onClick={() => setTargetBoard(key)}
                    disabled={submitting}
                  >
                    {BOARD_LABELS[key]}
                  </button>
                ))}
              </div>
            </div>

            {needsSort ? (
              <div className="admin-notice-post__field">
                <p className="admin-notice-post__label">{sortLabel} *</p>
                <div className="admin-notice-post__chips">
                  {sortOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`admin-notice-post__chip${selectedSort === option ? ' admin-notice-post__chip--on' : ''}`}
                      onClick={() => setSelectedSort(option)}
                      disabled={submitting}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {targetBoard === 'church' ? (
                  <p className="admin-board-manage__move-hint">
                    찬양대/방송/직원 게시판은 제목에 직무 키워드가 포함되어야 목록 필터에 표시됩니다. 필요 시 제목 앞에 직무가 자동으로 붙습니다.
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="admin-board-manage__move-hint admin-board-manage__move-hint--block">
                학교/기관/단체 게시판에는 별도 직무 분류가 없습니다.
              </p>
            )}
          </div>
        </div>

        <div className="admin-board-manage__modal-foot">
          <button
            type="button"
            className="admin-notice-post__btn admin-notice-post__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            취소
          </button>
          <button
            type="button"
            className="admin-notice-post__btn admin-notice-post__btn--primary"
            onClick={() => void movePost()}
            disabled={submitting}
          >
            {submitting ? '이동 중…' : '이동'}
          </button>
        </div>
      </div>
    </div>
  );
}
